import { useState, useEffect, useRef } from 'react';
import type { Person } from '../../types/timesheet.types';
import type { Order } from '../../types/ordering.types';
import { useMyOrders, useAllOrders, useFYIOrders, usePendingApprovals, useReviewedOrders } from '../../hooks/useOrders';
import { useConnector } from '../../contexts/ConnectorContext';
import { MyOrders } from './MyOrders';
import { AllOrders } from './AllOrders';
import { FYIQueue } from './FYIQueue';
import { ApprovalsQueue } from './ApprovalsQueue';
import { OrderDetail } from './OrderDetail';
import { ReviewedQueue } from './ReviewedQueue';
import { ChemicalOrders } from './ChemicalOrders';

type OrdTab = 'my' | 'chemical' | 'all' | 'fyi' | 'detail' | 'approvals' | 'reviewed';

interface Props {
  currentPerson: Person;
  onRefreshUser?: () => void;
  onRefreshSidebar?: () => void;
}

export function Ordering({ currentPerson: propPerson, onRefreshUser, onRefreshSidebar }: Props) {
  const connector = useConnector();
  const [tab, setTab] = useState<OrdTab>('my');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [prevTab, setPrevTab] = useState<OrdTab>('my');
  const [search, setSearch] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [localPerson, setLocalPerson] = useState<Person>(propPerson);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openingOrder, setOpeningOrder] = useState(false);

  // Refs so callbacks always see current values without stale closures
  const connectorRef = useRef(connector);
  connectorRef.current = connector;
  const onRefreshUserRef = useRef(onRefreshUser);
  onRefreshUserRef.current = onRefreshUser;

  const fetchFreshData = async (): Promise<Person> => {
    const [freshPerson, freshPeople] = await Promise.all([
      connectorRef.current.getCurrentUser(),
      connectorRef.current.getPeople({ active: true }),
    ]);
    setLocalPerson(freshPerson);
    setPeople(freshPeople);
    onRefreshUserRef.current?.();
    return freshPerson;
  };

  // Auto-refresh role + people on every mount (covers navigating from People section)
  useEffect(() => {
    let cancelled = false;
    setIsRefreshing(true);
    fetchFreshData()
      .catch(() => {})
      .finally(() => { if (!cancelled) setIsRefreshing(false); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { orders: myOrders, isLoading: myLoading, reload: reloadMy } = useMyOrders(localPerson.m365Email);
  const { orders: allOrders, isLoading: allLoading, reload: reloadAll } = useAllOrders();
  const { orders: fyiOrders, isLoading: fyiLoading, reload: reloadFYI } = useFYIOrders(localPerson.m365Email);
  const { orders: reviewedOrders, isLoading: reviewedLoading, reload: reloadReviewed } = useReviewedOrders(localPerson.m365Email);
  const { orders: pendingApprovals, isLoading: appLoading, reload: reloadApprovals } = usePendingApprovals(
    localPerson.m365Email,
    localPerson.purchaseApprovalLimit,
    {
      isCFO: localPerson.isCFO,
      isChemicalApprover: localPerson.isChemicalApprover,
      isOMGroup: localPerson.isOrderMgmtGroup,
      isOBOCEO: localPerson.canApproveOnBehalfOfCEO,
    }
  );

  const reloadAllOrders = () => {
    reloadMy(); reloadAll(); reloadApprovals(); reloadReviewed(); reloadFYI();
  };

  const switchTab = (t: OrdTab) => {
    setPrevTab(tab);
    setTab(t);
  };

  // Before opening an order, fetch fresh role data so security checks are current
  const openOrder = async (order: Order) => {
    setOpeningOrder(true);
    try {
      await fetchFreshData();
    } catch {}
    setOpeningOrder(false);
    setSelectedOrderId(order.id);
    setPrevTab(tab);
    setTab('detail');
  };

  const handleManualRefresh = async () => {
    if (isRefreshing || openingOrder) return;
    setIsRefreshing(true);
    try {
      await fetchFreshData();
      reloadAllOrders();
      onRefreshSidebar?.();
    } catch {}
    setIsRefreshing(false);
  };

  const crumb = {
    my: 'My Orders', chemical: 'Chemical Orders', all: 'All Orders', fyi: 'FYI Queue',
    detail: selectedOrderId ? 'Order Detail' : 'New Order', approvals: 'Needs Approval', reviewed: 'Reviewed',
  }[tab];

  return (
    <div className="ord-shell">
      {/* Top bar */}
      <div className="topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Ordering</span>
            <span className="sep">/</span>
            <span className="cur">{crumb}</span>
            <button
              className={`ord-crumb-refresh${isRefreshing ? ' spinning' : ''}`}
              onClick={handleManualRefresh}
              disabled={isRefreshing || openingOrder}
              title={isRefreshing ? 'Refreshing…' : 'Refresh role & orders'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          </div>
        </div>
        <div className="topbar-r">
          {tab !== 'detail' && (
            <div className="search-wrap" style={{ maxWidth: 220 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                placeholder="Search orders..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          )}
          {tab !== 'detail' && (
            <button className="btn btn-primary" onClick={() => { setSelectedOrderId(null); switchTab('detail'); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 14, height: 14 }}>
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New Order
            </button>
          )}
        </div>
      </div>

      {/* Sub-nav */}
      <div className="ord-subnav">
        <button className={`filter-tab${tab === 'my' ? ' active' : ''}`} onClick={() => switchTab('my')}>
          My Orders
        </button>
        {localPerson.isChemicalApprover && (
          <button className={`filter-tab${tab === 'chemical' ? ' active' : ''}`} onClick={() => switchTab('chemical')}>
            Chemical Orders
            {allOrders.filter(o => o.hasChemicalLines).length > 0 && (
              <span style={{ opacity: 0.7 }}> ({allOrders.filter(o => o.hasChemicalLines).length})</span>
            )}
          </button>
        )}
        <button className={`filter-tab${tab === 'all' ? ' active' : ''}`} onClick={() => switchTab('all')}>
          All Orders
        </button>
        <button className={`filter-tab${tab === 'fyi' ? ' active' : ''}`} onClick={() => switchTab('fyi')}>
          FYI Queue {fyiOrders.length > 0 && <span style={{ opacity: 0.7 }}>({fyiOrders.length})</span>}
        </button>
        {tab === 'detail' && (
          <button className="filter-tab active" style={{ pointerEvents: 'none' }}>
            Order Detail
          </button>
        )}
        {reviewedOrders.length > 0 && (
          <button
            className={`filter-tab${tab === 'reviewed' ? ' active' : ''}`}
            onClick={() => switchTab('reviewed')}
          >
            Reviewed {reviewedOrders.length > 0 && <span style={{ opacity: 0.7 }}>({reviewedOrders.length})</span>}
          </button>
        )}
        {(pendingApprovals.length > 0 || localPerson.isOrderMgmtGroup || localPerson.isChemicalApprover || localPerson.isCFO || localPerson.canApproveOnBehalfOfCEO) && (
          <button
            className={`filter-tab bluebutton${tab === 'approvals' ? ' active' : ''}`}
            style={{ marginLeft: 'auto' }}
            onClick={() => switchTab('approvals')}
          >
            Needs Approval
            {pendingApprovals.length > 0 && <span style={{ opacity: 0.7 }}> ({pendingApprovals.length})</span>}
          </button>
        )}
      </div>

      {/* Subviews — overlay spinner while opening an order */}
      <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {openingOrder && (
          <div className="ord-opening-overlay">
            <div className="ord-opening-spinner" />
            <span>Checking permissions…</span>
          </div>
        )}

        {tab === 'my' && (
          <MyOrders
            orders={myOrders}
            isLoading={myLoading}
            onOpenOrder={openOrder}
            search={search}
            people={people}
          />
        )}

        {tab === 'chemical' && (
          <ChemicalOrders
            orders={allOrders.filter(o => o.hasChemicalLines)}
            isLoading={allLoading}
            onOpenOrder={openOrder}
            search={search}
          />
        )}

        {tab === 'all' && (
          <AllOrders
            orders={allOrders}
            isLoading={allLoading}
            isOMGroup={localPerson.isOrderMgmtGroup}
            onOpenOrder={openOrder}
            search={search}
          />
        )}

        {tab === 'fyi' && (
          <FYIQueue
            orders={fyiOrders}
            isLoading={fyiLoading}
            onOpenOrder={openOrder}
            search={search}
          />
        )}

        {tab === 'approvals' && (
          <ApprovalsQueue
            orders={pendingApprovals}
            isLoading={appLoading}
            onOpenOrder={openOrder}
            search={search}
            people={people}
          />
        )}

        {tab === 'reviewed' && (
          <ReviewedQueue
            orders={reviewedOrders}
            isLoading={reviewedLoading}
            onOpenOrder={openOrder}
            search={search}
            currentPerson={localPerson}
          />
        )}

        {tab === 'detail' && (
          <OrderDetail
            orderId={selectedOrderId}
            currentPerson={localPerson}
            people={people}
            onSubmitted={() => {
              reloadAllOrders();
              onRefreshSidebar?.();
              setTab('my');
            }}
            onCreated={(order) => {
              reloadMy();
              reloadAll();
              setSelectedOrderId(order.id);
            }}
            onOrderChanged={() => {
              reloadAllOrders();
              onRefreshSidebar?.();
            }}
            onBack={() => {
              reloadAllOrders();
              onRefreshSidebar?.();
              setTab(prevTab);
            }}
          />
        )}
      </div>
    </div>
  );
}
